import { useLazyQuery, useMutation, useQuery } from '@apollo/react-hooks';
import Avatar from '@atlaskit/avatar';
import Button from '@atlaskit/button';
import PeopleGroupIcon from '@atlaskit/icon/glyph/people-group';
import Modal, { ModalBody, ModalFooter, ModalHeader, ModalTitle, ModalTransition } from '@atlaskit/modal-dialog';
import { Grid, GridColumn } from '@atlaskit/page';
import Select, { AsyncSelect } from '@atlaskit/select';
import Spinner from '@atlaskit/spinner';
import { keyBy } from 'lodash';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { PERSIST_WIDGET_PERMISSIONS } from '../../graphql/mutations';
import {
    GET_GROUPS_AND_USERS_FOR_PICKER_QUERY,
    GET_USERS_AND_GROUPS_DETAILS_QUERY,
    GET_WIDGET_PERMISSIONS_QUERY,
} from '../../graphql/queries';
import WithApolloClient from '../../hoc/WithApolloClient';
import { useFlagContext } from '../../hooks/useFlagsContext';
import { EDITOR } from '../../utils/permissionScopes';

const UsersAndGroupsSelect = WithApolloClient(({ client, value, onChange, omitOptions }) => {
    const loadOptions = useCallback(
        async (term) => {
            if ((term || '').length > 0) {
                return client
                    .query({ query: GET_GROUPS_AND_USERS_FOR_PICKER_QUERY, variables: { query: term } })
                    .then(({ data }) => {
                        const { groups, users } = data.getGroupsAndUsersForPicker;
                        const options = [];
                        if ((groups || {}).total > 0) {
                            options.push({
                                label: 'Groups',
                                options: groups.groups
                                    .filter((group) => !omitOptions.includes(group.groupId))
                                    .map((group) => ({ label: group.name, value: group.groupId, ...group })),
                            });
                        }
                        if ((users || {}).total > 0) {
                            options.push({
                                label: 'Users',
                                options: users.users
                                    .filter((user) => !omitOptions.includes(user.accountId))
                                    .map((user) => ({ label: user.displayName, value: user.accountId, ...user })),
                            });
                        }
                        return options;
                    });
            }
            return Promise.resolve([]);
        },
        [client, omitOptions],
    );

    return (
        <div style={{ width: '100%' }}>
            <Label>Select users and groups</Label>
            <AsyncSelect
                loadOptions={loadOptions}
                placeholder="Type user or group name"
                value={value}
                onChange={onChange}
            />
        </div>
    );
});

const Label = styled.label`
    width: 127px;
    height: 12px;
    font-size: 12px;
    font-weight: 400;
    font-stretch: normal;
    font-style: normal;
    line-height: 2.17;
    letter-spacing: -0.1px;
    text-align: left;
    color: #6b778c;
`;

const Form = styled.form`
    #user-group-select-container {
        display: flex;
        align-items: end;
        gap: 6px;
        margin-bottom: 16px;
        button {
            min-height: 40px;
        }
    }
    .widget-permission {
        width: 100%;
    }
    .widget-permission > div {
        align-items: center;
    }
    .widget-permission-select {
        margin: 3px 0px;
    }
`;

const AvatarContainer = styled.div`
    display: flex;
    align-items: center;
    > span.widget-description {
        width: 100%;
        height: 14px;
        font-size: 14px;
        font-weight: normal;
        font-stretch: normal;
        font-style: normal;
        padding-left: 4px;
        letter-spacing: normal;
        text-align: left;
        color: #172b4d;
        overflow: hidden;
        text-overflow: ellipsis;
    }
    > span.widget-group-icon {
        min-width: 36px;
    }
`;

const editorSelectOptions = [
    { label: 'Editor', value: EDITOR },
    { label: 'Remove access', value: '' },
];
const editorSelectOptionsByValue = keyBy(editorSelectOptions, ({ value }) => value);

const WidgetPermissionModal = ({ onClose, widgetId, managing }) => {
    const [userOrGroupSelected, setUserOrGroupSelected] = useState();
    const [permissions, setPermissions] = useState([]);
    const [users, setUsers] = useState({});
    const [groups, setGroups] = useState({});
    const [currentUser, setCurrentUser] = useState();
    const { pushFlag } = useFlagContext();

    const [runUsersAndGroupsQuery, getUsersAndGroupsQuery] = useLazyQuery(GET_USERS_AND_GROUPS_DETAILS_QUERY, {
        onError: (err) => pushFlag('Error', err.message.replace('GraphQL error: ', '')),
        onCompleted: (data) => {
            if (data && data.getUsersAndGroupsDetails) {
                setUsers((prev) => {
                    data.getUsersAndGroupsDetails.users.forEach((user) => {
                        prev[user.accountId] = user;
                    });
                    return { ...prev };
                });
                setGroups((prev) => {
                    data.getUsersAndGroupsDetails.groups.forEach((group) => {
                        prev[group.groupId] = group;
                    });
                    return { ...prev };
                });
            }
        },
    });
    const getWidgetPermissionsQuery = useQuery(GET_WIDGET_PERMISSIONS_QUERY, {
        variables: { widgetId },
        fetchPolicy: 'network-only',
        onError: (err) => pushFlag('Error', err.message.replace('GraphQL error: ', '')),
    });
    const [persistWidgetPermissions, persistWidgetPermissionsStatus] = useMutation(PERSIST_WIDGET_PERMISSIONS, {
        onError: (err) => pushFlag('Error', err.message.replace('GraphQL error: ', '')),
        onCompleted: () => {
            pushFlag('Success', 'Widget permissions saved with success!', 'success');
            onClose();
        },
    });

    useEffect(() => {
        if (!getUsersAndGroupsQuery.loading) {
            const userIds = [];
            const groupIds = [];
            permissions.forEach(({ userAccountId, groupId }) => {
                if (userAccountId && !users[userAccountId]) {
                    userIds.push(userAccountId);
                }
                if (groupId && !groups[groupId]) {
                    groupIds.push(groupId);
                }
            });
            if (userIds.length || groupIds.length) {
                runUsersAndGroupsQuery({ variables: { userIds, groupIds } });
            }
        }
    }, [getUsersAndGroupsQuery.loading, groups, permissions, runUsersAndGroupsQuery, users]);

    useEffect(() => {
        if (
            getWidgetPermissionsQuery.called &&
            !getWidgetPermissionsQuery.loading &&
            getWidgetPermissionsQuery.data?.getWidgetPermissions
        ) {
            setPermissions(getWidgetPermissionsQuery.data.getWidgetPermissions);
        }
    }, [getWidgetPermissionsQuery.called, getWidgetPermissionsQuery.data, getWidgetPermissionsQuery.loading]);

    const onAdd = useCallback(() => {
        const permission = { scopes: EDITOR };
        if (userOrGroupSelected.groupId) {
            permission.groupId = userOrGroupSelected.groupId;
            setGroups((prev) => ({ ...prev, [userOrGroupSelected.groupId]: userOrGroupSelected }));
        } else {
            permission.userAccountId = userOrGroupSelected.accountId;
            setUsers((prev) => ({ ...prev, [userOrGroupSelected.accountId]: userOrGroupSelected }));
        }
        setPermissions([...permissions, permission]);
        setUserOrGroupSelected(null);
    }, [permissions, userOrGroupSelected]);

    const selected = useMemo(
        () => permissions.map((permission) => permission.groupId || permission.userAccountId),
        [permissions],
    );

    const onRemoveAccess = useCallback((userAccountIdOrGroupId) => {
        setPermissions((prev) =>
            prev.filter(
                (permission) => ![permission.groupId, permission.userAccountId].includes(userAccountIdOrGroupId),
            ),
        );
    }, []);

    useEffect(() => {
        if (window.AP) {
            window.AP.getCurrentUser((user) => setCurrentUser(user.atlassianAccountId));
        }
    }, []);

    const onSubmit = useCallback(
        (e) => {
            e.preventDefault();
            persistWidgetPermissions({
                variables: {
                    widgetId,
                    permissions: permissions.map(({ id, userAccountId, groupId, scopes }) => ({
                        id,
                        userAccountId,
                        groupId,
                        scopes,
                    })),
                },
            });
        },
        [permissions, persistWidgetPermissions, widgetId],
    );

    return (
        <ModalTransition>
            <Modal shouldScrollInViewport onClose={onClose} width="small">
                <Form onSubmit={onSubmit}>
                    <ModalHeader>
                        <ModalTitle>{`${managing ? 'Manage' : 'Add'} widget editors`}</ModalTitle>
                    </ModalHeader>

                    <ModalBody>
                        <Grid layout="fluid" spacing="compact">
                            <GridColumn>
                                <div id="user-group-select-container">
                                    <UsersAndGroupsSelect
                                        value={userOrGroupSelected}
                                        onChange={setUserOrGroupSelected}
                                        omitOptions={selected}
                                    />
                                    <Button isDisabled={!userOrGroupSelected} onClick={onAdd}>
                                        Add
                                    </Button>
                                </div>
                            </GridColumn>
                        </Grid>
                        {permissions.map((widgetPermission) => (
                            <div
                                className="widget-permission"
                                key={widgetPermission.userAccountId || widgetPermission.groupId}
                            >
                                <Grid layout="fluid" spacing="compact">
                                    <GridColumn medium={8}>
                                        <AvatarContainer>
                                            {widgetPermission.groupId && (
                                                <span className="widget-group-icon">
                                                    <PeopleGroupIcon size="large" />
                                                </span>
                                            )}
                                            {widgetPermission.userAccountId && (
                                                <Avatar src={users[widgetPermission.userAccountId]?.avatarUrl} />
                                            )}
                                            <span
                                                className="widget-description"
                                                title={widgetPermission.groupId || widgetPermission.userAccountId}
                                            >
                                                {widgetPermission.groupId && groups[widgetPermission.groupId]?.name}
                                                {widgetPermission.userAccountId &&
                                                    users[widgetPermission.userAccountId]?.displayName}
                                                {widgetPermission.userAccountId === currentUser && ' (you)'}
                                            </span>
                                        </AvatarContainer>
                                    </GridColumn>
                                    <GridColumn medium={4}>
                                        <Select
                                            isDisabled={widgetPermission.userAccountId === currentUser}
                                            className="widget-permission-select"
                                            options={editorSelectOptions}
                                            value={editorSelectOptionsByValue[widgetPermission.scopes || '']}
                                            onChange={(selectedOption) =>
                                                selectedOption?.value === '' &&
                                                onRemoveAccess(
                                                    widgetPermission.groupId || widgetPermission.userAccountId,
                                                )
                                            }
                                        />
                                    </GridColumn>
                                </Grid>
                            </div>
                        ))}
                    </ModalBody>
                    <ModalFooter>
                        <Button isDisabled={persistWidgetPermissionsStatus.loading} onClick={onClose}>
                            Cancel
                        </Button>
                        <Button
                            type="submit"
                            isDisabled={persistWidgetPermissionsStatus.loading}
                            appearance="primary"
                            iconAfter={persistWidgetPermissionsStatus.loading ? <Spinner size="small" /> : null}
                        >
                            Save
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </ModalTransition>
    );
};

export default WidgetPermissionModal;
